import React, { useMemo, useState } from 'react';
import { Dropdown, Space } from 'antd';
import { Button, MenuDotsVertical } from 'pink-lava-ui';
import { BookTemplateIcon, DownloadIcon, UploadIcon } from 'lucide-react';
import { UploadModal } from '@/components/ui/modal/upload/UploadModal';
import { generateImportTemplate, triggerDownload, type TemplateConfig } from '@/utils/download';
import { useAuth } from '@/context/authContext';
import dayjs from 'dayjs';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useNotification } from '@/context/notificationContext';
import { useDownloadData } from '@/mutations';

export interface DownloadUploadActionsProps {
  downloadDataParams?: any;
  showDownloadData?: boolean;
  showDownloadTemplate?: boolean;
  showUpload?: boolean;
  uploadTitle?: string;
  uploadAccept?: string;
  uploadMaxCount?: number;
  uploadMaxSize?: number;
  className?: string;
  excelTemplate?: Omit<TemplateConfig, 'data'>;
  upload?: {
    url: string;
    queryKey?: string;
  };
  download?: {
    url: string;
    params?: any;
  };
  resourceName?: string;
}

export const DownloadUploadActions: React.FC<DownloadUploadActionsProps> = ({
  showDownloadData = true,
  showDownloadTemplate = true,
  showUpload = true,
  uploadTitle = 'Upload',
  uploadAccept = '.xlsx,.xls',
  uploadMaxCount = 1,
  uploadMaxSize = 5,
  className = '',
  excelTemplate,
  upload,
  download,
  resourceName,
}) => {
  const showNotification = useNotification();
  const { user } = useAuth();
  const { isMobile } = useBreakpoint();
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const { mutate: downloadData } = useDownloadData({
    meta: {
      message: 'Gagal download data, silahkan coba lagi.',
    },
  });

  const handleDownloadTemplate = async () => {
    try {
      await generateImportTemplate({
        name: `${user!.vendor.code}_TMPL${excelTemplate?.name ? `_${excelTemplate.name}` : ''}_${dayjs().format('YYYYMMDD')}`,
        headers: excelTemplate?.headers!,
        referenceData: excelTemplate?.referenceData!,
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleDownloadData = async () => {
    downloadData(
      {
        url: download?.url!,
        params: download?.params,
      },
      {
        onSuccess: ({ data, filename }) => {
          triggerDownload(data, filename);
          showNotification.success({
            message: 'Data berhasil didownload.',
          });
        },
        onError: error => {
          console.log('Download error: ', error);
        },
      }
    );
  };

  // Handle menu item clicks
  const handleMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'download-data':
        handleDownloadData();
        break;
      case 'download-template':
        handleDownloadTemplate();
        break;
      case 'upload-data':
        setIsUploadModalVisible(true);
        break;
    }
  };

  // Build menu items based on visibility props
  const menuItems = useMemo(() => {
    const items = [];

    if (showDownloadData && download) {
      items.push({
        icon: <DownloadIcon size={16} />,
        key: 'download-data',
        label: 'Download Data',
      });
    }

    if (showDownloadTemplate && excelTemplate) {
      items.push({
        icon: <BookTemplateIcon size={16} />,
        key: 'download-template',
        label: 'Download Template',
      });
    }

    if (showUpload && upload?.url && excelTemplate) {
      items.push({
        icon: <UploadIcon size={16} />,
        key: 'upload-data',
        label: 'Upload Data',
      });
    }

    return items;
  }, [showDownloadData, download, showDownloadTemplate, excelTemplate, showUpload, upload]);

  // Don't render if no actions are available
  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      <Space>
        <Dropdown
          menu={{
            items: menuItems,
            onClick: handleMenuClick,
          }}
        >
          {isMobile ? (
            <Button shape="circle" type="tertiary">
              <MenuDotsVertical />
            </Button>
          ) : (
            <Button type="tertiary" shape="round" icon={<DownloadIcon />} className={className}>
              Download / Upload
            </Button>
          )}
        </Dropdown>
      </Space>

      {/* Upload Modal */}
      {showUpload && upload && (
        <UploadModal
          uploadUrl={upload.url}
          queryKey={upload.queryKey}
          visible={isUploadModalVisible}
          title={uploadTitle}
          accept={uploadAccept}
          maxCount={uploadMaxCount}
          maxSize={uploadMaxSize}
          onCancel={() => setIsUploadModalVisible(false)}
          excelTemplate={excelTemplate}
          resourceName={resourceName}
        />
      )}
    </>
  );
};

export default DownloadUploadActions;
